import React, { useState, useMemo } from "react";
import {
  GridColumnMenuSort,
  GridColumnMenuFilter,
  GridColumnMenuItemGroup,
  GridColumnMenuItem,
  GridColumnMenuItemContent,
  GridColumnMenuProps
} from "@progress/kendo-react-grid";

interface ColumnInterface {
  title?: string;
  field?: string;
  filter?: "boolean" | "numeric" | "text" | "date";
  minWidth?: number;
  minGridWidth?: number;
  locked?: boolean;
  width?: string | number;
}

export interface ColumnMenuProps extends GridColumnMenuProps {
  columns: ColumnInterface[];
  defaultColumnIds: string[];
  visibleColumns: ColumnInterface[];
  onColumnsSubmit: (columns: ColumnInterface[]) => void;
}

export const ColumnMenu = (props: ColumnMenuProps) => {
  const [visibleColumns, setVisibleColumns] = useState<Array<ColumnInterface>>(
    props.visibleColumns
  );
  
  // Sort columns first by those selected, then alphabetically
  const sortedColumns = useMemo<Array<ColumnInterface>>(() => {
    return [...props.columns].sort((columnA, columnB) => {
      const isASelected = visibleColumns.some(column => column.field === columnA.field);
      const isBSelected = visibleColumns.some(column => column.field === columnB.field);
      if (isASelected === isBSelected) {
        if (isASelected && isBSelected) {
          const columnAIndex = visibleColumns.findIndex(column => column.field === columnA.field);
          const columnBIndex = visibleColumns.findIndex(column => column.field === columnB.field);
          return columnAIndex > columnBIndex ? 1 : -1;
        }
        else if (columnA.title && columnB.title) {
          return columnA.title > columnB.title ? 1 : -1;
        }
        else {
          return 0;
        }
      }
     
      return isASelected > isBSelected ? -1 : 1;
    });
  }, []);
  
  const [columnsExpanded, setColumnsExpanded] = React.useState<boolean>(false);
  const [filterExpanded, setFilterExpanded] = React.useState<boolean>(false);
  const [columnClicked, setColumnClicked] = React.useState<string | undefined>();

  const onToggleColumn = (columnId: string) => {
    const targetColumn = props.columns.find(column => column.field === columnId);
    if (!targetColumn) {
      return;
    }

    const index = visibleColumns.findIndex((item) => item.field === columnClicked);

    const newVisibleColumns = visibleColumns.includes(targetColumn) ? visibleColumns.filter(column => column.field !== targetColumn.field) : [...visibleColumns.slice(0, index + 1), targetColumn, ...visibleColumns.slice(index + 1)];
    setVisibleColumns(newVisibleColumns);
  };

  const onReset = (event: React.FormEvent) => {
    event.preventDefault();
    const defaultColumns = props.columns.filter(column => props.defaultColumnIds.includes(column.field || ""));
    setVisibleColumns(defaultColumns);
    props.onColumnsSubmit(defaultColumns);
    if (props.onCloseMenu) {
      props.onCloseMenu();
    }
  };

  const onSubmit = (event: React.FormEvent) => {
    if (event) {
      event.preventDefault();
    }
    props.onColumnsSubmit(visibleColumns);
    if (props.onCloseMenu) {
      props.onCloseMenu();
    }
  };

  const onMenuItemClick = () => {
    const value = !columnsExpanded;
    setColumnsExpanded(value);
    setFilterExpanded(value ? false : filterExpanded);
    setColumnClicked(props.column.field);
  };

  const onFilterExpandChange = (value: boolean) => {
    setFilterExpanded(value);
    setColumnsExpanded(value ? false : columnsExpanded);
  };

  const oneVisibleColumn = visibleColumns.length === 1;
  
  return (
    <div>
      <GridColumnMenuSort {...props} />
      <GridColumnMenuFilter
        {...props}
        onExpandChange={onFilterExpandChange}
        expanded={filterExpanded}
      />
      <GridColumnMenuItemGroup>
        <GridColumnMenuItem
          title={"Columns"}
          iconClass={"k-i-columns"}
          onClick={onMenuItemClick}
        />
        <GridColumnMenuItemContent show={columnsExpanded}>
          <div className={"k-column-list-wrapper"}>
            <form onSubmit={onSubmit} onReset={onReset}>
              <div className={"k-column-list"}>
                {sortedColumns.map((column) => (
                  <div key={`column-${column.field}`} className={"k-column-list-item"}>
                    <span>
                      <input
                        id={`column-visiblity-show-${column.field}`}
                        className="k-checkbox k-checkbox-md k-rounded-md"
                        type="checkbox"
                        readOnly={true}
                        disabled={visibleColumns.some(c => c.field === column.field) && oneVisibleColumn}
                        checked={visibleColumns.some(c => c.field === column.field)}
                        onClick={() => {
                          onToggleColumn(column.field || "");
                        }}
                      />
                      <label
                        htmlFor={`column-visiblity-show-${column.field}`}
                        className="k-checkbox-label"
                        style={{ userSelect: "none" }}
                      >
                        {column.title}
                      </label>
                    </span>
                  </div>
                ))}
              </div>
              <div className={"k-actions k-hstack k-justify-content-stretch"}>
                <button
                  type={"reset"}
                  className={
                    "k-button k-button-md k-rounded-md k-button-solid k-button-solid-base"
                  }
                >
                  Reset
                </button>
                <button
                  className={
                    "k-button k-button-md k-rounded-md k-button-solid k-button-solid-primary"
                  }
                >
                  Save
                </button>
              </div>
            </form>
          </div>
        </GridColumnMenuItemContent>
      </GridColumnMenuItemGroup>
    </div>
  );
};
